import {createSlice} from "@reduxjs/toolkit";
import * as yup from "yup";
import _ from "lodash";

const TABLESTATES_KEY = "tableStates";
export const getDataTableStates = () => {
    try {
        return JSON.parse(window.localStorage.getItem(TABLESTATES_KEY) ?? `[]`);
    } catch (e) {
        return [];
    }
}
/**
 * Writes the table states to localStorage. Kept outside the reducer: reducers
 * must be pure (Immer + devtools time-travel), and a storage write is a side
 * effect.
 */
export const persistDataTableStates = (tableStates: DataTableState[]): void => {
    try {
        window.localStorage.setItem(TABLESTATES_KEY, JSON.stringify(tableStates ?? []));
    } catch (e) {
        // Storage disabled/full: losing the table layout preference is harmless.
    }
}
export const dataTableStateSchema = yup.object().shape({
    dataTableId: yup.string(),
    etat: yup.mixed<object>(),
    updateId: yup.number(),
});
export type DataTableState = yup.InferType<typeof dataTableStateSchema>;
export const dataTableStateImpl : DataTableState= {
    dataTableId: '',
    etat: {},
    updateId: 0,
}

export const dataTableStatesListSchema = yup.object().shape({
    tableStates: yup.array().of(yup.mixed<DataTableState>()).default([]).required(),
});
export type DataTableStatesList = yup.InferType<typeof dataTableStatesListSchema>;


const initialState = { tableStates: []} satisfies DataTableStatesList as DataTableStatesList;

const dataTableSlice = createSlice({
    name: 'dataTable',
    initialState: initialState,
    reducers:{
        clearDataTableState: (state) => {
            return {...initialState};
        },
        initDataTableState: (state, action) => {
            state.tableStates = action.payload;
        },
        setDataTableState:(state, action ) => {
            const datas = _.cloneDeep(state.tableStates ?? []);
            let index = _.findIndex(datas, {"dataTableId": action.payload.dataTableId});

            const data : any  = index >= 0 ? datas[index] : _.cloneDeep(dataTableStateImpl);
            data.dataTableId = action.payload.dataTableId;
            data.etat = action.payload.etat;
            data.updateId = action.payload.updateId;

            index = index >= 0 ? index : _.size(datas) ;
            datas[index] = data;

            state.tableStates = datas;
        },
    }
})

export const { clearDataTableState, initDataTableState, setDataTableState } = dataTableSlice.actions;

/**
 * Persists one table's UI state (density, ordering, filters…) and mirrors the
 * resulting list to localStorage. Use this instead of `setDataTableState`
 * directly so the persistence stays in one place.
 */
export const saveDataTableState = (payload: DataTableState) =>
    (dispatch: (action: any) => void, getState: () => any) => {
        dispatch(setDataTableState(payload));
        persistDataTableStates(getState().dataTable?.tableStates ?? []);
    };

export default dataTableSlice.reducer;
